import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { showToast } from '../utils/Toast';
import { config } from '../../config/config';

const QuickbooksCallback = () => {
  const [status, setStatus] = useState('processing');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we're in a popup window
    const isPopup = window.opener && window.opener !== window;
    
    // Log the full URL and search params for debugging
    console.log('QuickBooks callback URL:', location.pathname + location.search);
    console.log('Is popup window:', isPopup);
    
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const realmId = params.get('realmId');
    const state = params.get('state');
    const success = params.get('success') === 'true';
    const error = params.get('error');
    
    console.log('QuickBooks callback params:', { code, realmId, state, success, error });

    // If we have code and realmId, create token on the server
    if (code && realmId) {
      setStatus('processing');
      
      // Call the server to create a token
      fetch(`${config.apiUrl}/api/quickbooks/create-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ code, realmId, state })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setStatus('success');
          
          if (isPopup) {
            // Close the popup window after a short delay
            setTimeout(() => {
              window.close();
            }, 1500);
          } else {
          showToast('QuickBooks connected successfully!', 'success');
          setTimeout(() => {
            navigate(-1, { fallback: '/' });
          }, 2000);
          }
        } else {
          setStatus('error');
          const errorMessage = data.message || 'Unknown error';
          console.error('QuickBooks connection failed:', errorMessage);
          if (!isPopup) {
          showToast(`QuickBooks connection failed: ${errorMessage}`, 'error');
          }
        }
      })
      .catch(err => {
        setStatus('error');
        console.error('Error creating QuickBooks token:', err);
        if (!isPopup) {
        showToast(`Error creating QuickBooks token: ${err.message}`, 'error');
        }
      });
    } else if (success) {
      setStatus('success');
      
      if (isPopup) {
        // Close the popup window after a short delay
        setTimeout(() => {
          window.close();
        }, 1500);
      } else {
      showToast('QuickBooks connected successfully!', 'success');
      setTimeout(() => {
        navigate(-1, { fallback: '/' });
      }, 2000);
      }
    } else if (error) {
      setStatus('error');
      console.error('QuickBooks connection failed:', error);
      if (!isPopup) {
      showToast(`QuickBooks connection failed: ${error}`, 'error');
      }
    }
  }, [location, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            QuickBooks Connection
          </h2>
          
          {status === 'processing' && (
            <div className="mt-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Processing QuickBooks connection...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="mt-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600">QuickBooks connected successfully!</p>
              <p className="mt-2 text-sm text-gray-500">Redirecting you back...</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="mt-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600">QuickBooks connection failed.</p>
              <button 
                onClick={() => navigate(-1)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickbooksCallback;
